import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router-dom';
import { ErrorNotice, FactList, Heading, PageHeading, Panel, SkeletonTable, useReturnTo } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { SALES_CONTACTS_PATH, readSalesContact, type ContactRow } from '@/lib/sales-data';
import { formatDay, isNotAllowed } from '@/lib/sales-workspace';
import { AccountLink, ContactFlags, DealTable, EngagementTimeline } from '@/components/sales-workspace/sales-records';

/** What the do-not-contact banner says; null when they may be emailed and called. */
export function doNotContactNotice(contact: Pick<ContactRow, 'emailOptOut' | 'doNotCall'>): { readonly title: string; readonly message: string } | null {
  if (contact.emailOptOut && contact.doNotCall) {
    return { title: 'Do not email or call', message: 'They asked not to be emailed or called. Reach the account through someone else there.' };
  }
  if (contact.emailOptOut) return { title: 'Do not email', message: 'They opted out of email. Call instead, and leave them off any email outreach.' };
  if (contact.doNotCall) return { title: 'Do not call', message: 'They asked not to be called. Email instead.' };
  return null;
}

/**
 * One contact for a rep: how to reach them (and whether they may be), the
 * deals they are on with their role, and every recent touch with them.
 *
 * Dark: the API answers 403 to anyone off the sales workspace allow-list.
 *
 * @returns The page.
 */
export function SalesContactPage(): React.ReactElement {
  const { contactId = '' } = useParams();
  const backTo = useReturnTo(SALES_CONTACTS_PATH);
  const detail = useQuery({
    queryKey: ['sales-contact', contactId],
    queryFn: ({ signal }) => readSalesContact(contactId, signal),
    retry: (count, error) => !isNotAllowed(error) && count < 2,
  });

  if (detail.isError && isNotAllowed(detail.error)) return <Navigate to="/events" replace />;
  if (detail.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={6} columns={['w-[30%]', 'w-[50%]']} label="Loading the contact…" />
      </section>
    );
  }
  if (detail.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="SALES" backTo={backTo} title="Contact" />
        <ErrorNotice title="Contact unavailable" message={errorMessage(detail.error)} />
      </section>
    );
  }

  const { contact, deals, timeline } = detail.data;
  const warning = doNotContactNotice(contact);
  const roles = new Map(deals.map((entry) => [entry.deal.id, [entry.role, entry.isPrimary ? 'Primary' : ''].filter(Boolean).join(' · ')]));

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        backTo={backTo}
        title={contact.name || 'Unnamed contact'}
        subtitle={[contact.title, contact.accountName].filter(Boolean).join(' · ')}
      />
      {warning ? (
        <div className="mb-4">
          <ErrorNotice inline tone="warning" title={warning.title} message={warning.message} action={<ContactFlags contact={contact} />} />
        </div>
      ) : null}

      <Panel className="p-5">
        <Heading variant="card" as="h2" className="mb-3">
          About
        </Heading>
        <FactList
          columns={3}
          facts={[
            {
              label: 'Email',
              value: !contact.email ? '—' : contact.emailOptOut ? `${contact.email} (opted out)` : (
                <a href={`mailto:${contact.email}`} className="text-brand hover:underline">
                  {contact.email}
                </a>
              ),
            },
            {
              label: 'Phone',
              value: !contact.phone ? '—' : contact.doNotCall ? `${contact.phone} (do not call)` : (
                <a href={`tel:${contact.phone}`} className="text-brand hover:underline">
                  {contact.phone}
                </a>
              ),
            },
            { label: 'Account', value: <AccountLink id={contact.accountId} name={contact.accountName} /> },
            { label: 'Title', value: contact.title || '—' },
            { label: 'Owner', value: contact.ownerName || '—' },
            { label: 'Last activity', value: formatDay(contact.lastActivityDate, { withYear: true }) },
          ]}
        />
      </Panel>

      <div className="mt-6">
        <Heading variant="card" as="h2" className="mb-3">
          Their deals
        </Heading>
        <DealTable
          deals={deals.map((entry) => entry.deal)}
          role={roles}
          prefix="deals"
          defaultSort={{ key: 'eventDate', direction: 'desc' }}
          emptyTitle="Not on any deal yet"
          emptyMessage="Deals list this person once they are added as a contact role in Salesforce."
        />
      </div>

      <div className="mt-6">
        <Heading variant="card" as="h2" className="mb-3">
          Engagement
        </Heading>
        <Panel className="p-5">
          <EngagementTimeline items={timeline} hideContact emptyMessage="Emails, calls and meetings with this person show here." />
        </Panel>
      </div>
    </section>
  );
}
